import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { StatusDotComponent, StatusDotStatus } from 'src/app/ui-kit/status-dot/status-dot.component';

/** Avatar size variants. */
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<AvatarSize, number> = { xs: 24, sm: 32, md: 40, lg: 56, xl: 80 };

/**
 * Deterministic palette of brand backgrounds for initial avatars. Each entry
 * pairs the Tailwind background class with the matching Ionic contrast text
 * token, so dark fills (purple, slate, vermilion, moss) get white initials
 * and light fills (sand, amber, aqua, lavender, sage, coral) get dark
 * initials — without the avatar having to know which is which.
 */
interface PaletteEntry {
  bg: string;
  text: string;
}

const PALETTE: PaletteEntry[] = [
  { bg: 'bg-purple', text: 'text-[var(--ion-color-purple-contrast)]' },
  { bg: 'bg-lavender', text: 'text-[var(--ion-color-lavender-contrast)]' },
  { bg: 'bg-coral', text: 'text-[var(--ion-color-coral-contrast)]' },
  { bg: 'bg-amber', text: 'text-[var(--ion-color-amber-contrast)]' },
  { bg: 'bg-aqua', text: 'text-[var(--ion-color-aqua-contrast)]' },
  { bg: 'bg-slate', text: 'text-[var(--ion-color-slate-contrast)]' },
  { bg: 'bg-sand', text: 'text-[var(--ion-color-sand-contrast)]' },
  { bg: 'bg-vermilion', text: 'text-[var(--ion-color-vermilion-contrast)]' },
  { bg: 'bg-sage', text: 'text-[var(--ion-color-sage-contrast)]' },
  { bg: 'bg-moss', text: 'text-[var(--ion-color-moss-contrast)]' },
];

/**
 * Student / practitioner avatar with initials fallback and optional status dot overlay.
 *
 * @security FERPA — `name` is treated as student PII. Do not log the `name` input, do
 * not embed it in URLs, and never expose it in error messages or telemetry.
 *
 * @example
 * <app-avatar name="Ada Lovelace" imageUrl="/students/ada.jpg" size="md" />
 *
 * @example
 * <app-avatar name="Grace Hopper" size="lg" status="on-track" />
 *
 * @feature shared
 */
@Component({
  selector: 'app-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [StatusDotComponent],
  host: { class: 'inline-block relative' },
  template: `
    <div
      class="relative inline-flex items-center justify-center rounded-full overflow-hidden"
      [style.width.px]="pixels()"
      [style.height.px]="pixels()"
      [class]="bgClass()"
      [attr.role]="'img'"
      [attr.aria-label]="name()"
    >
      @if (imageUrl() && !imageFailed()) {
        <img [src]="imageUrl()" [alt]="name()" class="w-full h-full object-cover" (error)="onImageError()" />
      } @else {
        <span class="font-semibold select-none" [style.font-size.px]="fontPx()">
          {{ initials() }}
        </span>
      }
    </div>

    @if (status()) {
      <span
        class="absolute bottom-0 right-0 inline-flex h-4 w-4 items-center justify-center rounded-full border border-outline bg-surface shadow-[var(--theme-shadow)]"
      >
        <app-status-dot [status]="status()!" size="sm" [ariaLabel]="name() + ' status'" />
      </span>
    }
  `,
})
export class AvatarComponent {
  /** Full name. Used for initials fallback and alt text. @security FERPA — treated as PII. */
  readonly name = input.required<string>();

  /** Optional image URL. If loading fails, falls back to initials. */
  readonly imageUrl = input<string | undefined>(undefined);

  /**
   * Optional override for the color-hashing seed. When provided, the avatar
   * background palette index is derived from this string instead of `name`.
   * Useful for callers that want a richer seed (e.g. first + middle + last)
   * without changing the displayed name. Hashing is case-insensitive.
   */
  readonly colorSeed = input<string | undefined>(undefined);

  /** Size variant. @default 'md' */
  readonly size = input<AvatarSize>('md');

  /** Optional status dot overlay. */
  readonly status = input<StatusDotStatus | undefined>(undefined);

  /** Tracks whether the image failed to load so we can fall back to initials. */
  readonly imageFailed = signal<boolean>(false);

  readonly pixels = computed(() => SIZE_PX[this.size()]);
  readonly fontPx = computed(() => Math.round(SIZE_PX[this.size()] * 0.4));

  /** Up-to-2 uppercase initials derived from the full name. */
  readonly initials = computed(() => {
    const parts = (this.name() ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  });

  /**
   * Deterministic background + contrast-text classes derived from a
   * case-insensitive hash of the seed (`colorSeed` when provided, otherwise
   * `name`). Returned as a single space-joined string so a single `[class]`
   * binding paints both the wrapper bg and the inherited initials color.
   */
  readonly bgClass = computed(() => {
    if (this.imageUrl() && !this.imageFailed()) return 'bg-surface-medium';
    const seed = (this.colorSeed() ?? this.name() ?? '').toLowerCase();
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    const entry = PALETTE[Math.abs(hash) % PALETTE.length];
    return `${entry.bg} ${entry.text}`;
  });

  onImageError(): void {
    this.imageFailed.set(true);
  }
}
